import React, { useEffect, useState } from "react";
import NotificationBell from "./NotificationBell";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  LayoutDashboard, CalendarPlus, TicketCheck, Stamp, Printer, ScanLine,
  Search, Database, FileBarChart, Landmark, Flame, LogOut, Home, ShieldCheck,
  Shirt, Warehouse, PackageOpen, FileText, Receipt, Store, FileSearch, ChevronDown, ChevronsLeft, ChevronsRight, History, Soup
, FilePlus2, BadgeCheck, Car, Baby, UserRound, ClipboardCheck, Building2, MapPin, Sparkles, QrCode, Calculator, ChevronLeft } from "lucide-react";
import { Button } from "./ui/button";
import SareeIcon from "./SareeIcon";
import { userPages } from "../lib/pages";
import { readCache, writeCache } from "../lib/brandCache";
import MobileHome from "./MobileHome";
import DownloadAppButton from "./DownloadAppButton";

// Counter users can issue tickets only during their assigned shift (IST); outside it, say so on every page.
function OffShiftBanner({ user }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 30000); return () => clearInterval(t); }, []);
  if (user.role !== "COUNTER_USER" || !user.shift_start || !user.shift_end) return null;
  const now = new Date(Date.now() + 330 * 60000).toISOString().slice(11, 16);
  const st = user.shift_start, en = user.shift_end;
  const open = st < en ? st <= now && now < en : now >= st || now < en;
  if (open) return null;
  return (
    <div className="mb-4 rounded-md border border-orange-300 bg-orange-50 px-4 py-2.5 text-sm text-orange-900" data-testid="off-shift-banner">
      Your shift is <b>{user.shift_name} ({st}–{en})</b>. Tickets can be issued only during your shift time.
    </div>
  );
}

const ALL = ["SUPER_ADMIN", "TEMPLE_ADMIN", "SUPERVISOR", "COUNTER_USER", "VERIFICATION_USER"];
const NAV = [
  { to: "/", label: "Protocol Dashboard", icon: LayoutDashboard, roles: ALL, end: true, module: "PROTOCOL", page: "PROTOCOL_DASHBOARD" },
  { section: "Protocol" },
  { to: "/protocol/advanced", label: "Advanced Booking", icon: CalendarPlus, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "COUNTER_USER"], module: "PROTOCOL", page: "ADVANCED" },
  { to: "/protocol/current", label: "Current Booking", icon: TicketCheck, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "COUNTER_USER"], module: "PROTOCOL", page: "CURRENT" },
  { to: "/protocol/pending", label: "Pending Approvals", icon: Stamp, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "SUPERVISOR"], module: "PROTOCOL", page: "PENDING" },
  { to: "/protocol/issue", label: "Ticket Issue", icon: Printer, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "COUNTER_USER"], module: "PROTOCOL", page: "ISSUE" },
  { to: "/protocol/verify", label: "Verification", icon: ScanLine, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "VERIFICATION_USER"], module: "PROTOCOL", page: "VERIFY" },
  { to: "/protocol/transactions", label: "Transactions", icon: Search, roles: ALL, module: "PROTOCOL", page: "TRANSACTIONS" },
  { to: "/reports", label: "Protocol Reports", icon: FileBarChart, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "SUPERVISOR", "COUNTER_USER"], module: "PROTOCOL", page: "PROTOCOL_REPORTS" },
  { to: "/protocol/shift-closing", label: "Shift DCR Closing", icon: Calculator, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "SUPERVISOR", "COUNTER_USER"], module: "PROTOCOL", page: "PROTOCOL_SHIFT_CLOSING" },
  { section: "Sarees" },
  { to: "/sarees/dashboard", label: "Saree Dashboard", icon: LayoutDashboard, roles: ALL, module: "SAREES", page: "SAREE_DASHBOARD" },
  { to: "/sarees/request", label: "Receiving Counter", icon: SareeIcon, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "COUNTER_USER"], module: "SAREES", page: "SAREE_REQUEST" },
  { to: "/sarees/godown-in", label: "Godown In", icon: Warehouse, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "VERIFICATION_USER", "COUNTER_USER"], module: "SAREES", page: "SAREE_GODOWN_IN" },
  { to: "/sarees/godown-out", label: "Godown Out", icon: PackageOpen, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "VERIFICATION_USER", "COUNTER_USER"], module: "SAREES", page: "SAREE_GODOWN_OUT" },
  { to: "/sarees/sales", label: "Contract Sales", icon: Receipt, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "COUNTER_USER"], module: "SAREES", page: "SAREE_SALES" },
  { to: "/sarees/devasthanam-sales", label: "Devasthanam Sales", icon: Store, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "COUNTER_USER"], module: "SAREES", page: "SAREE_DEVASTHANAM" },
  { to: "/sarees/reports", label: "Saree Reports", icon: FileText, roles: ALL, module: "SAREES", page: "SAREE_REPORTS" },
  { to: "/sarees/shift-closing", label: "Shift DCR Closing", icon: Calculator, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "SUPERVISOR", "COUNTER_USER"], module: "SAREES", page: "SAREE_SHIFT_CLOSING" },
  { section: "Annaprasadam" },
  { to: "/anna/dashboard", label: "Annaprasadam Dashboard", icon: LayoutDashboard, roles: ALL, module: "ANNAPRASADAM", page: "ANNA_DASHBOARD" },
  { to: "/anna/issue", label: "Annaprasadam Issue", icon: Soup, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "COUNTER_USER"], module: "ANNAPRASADAM", page: "ANNA_ISSUE" },
  { to: "/anna/verify", label: "Annaprasadam Verify", icon: ScanLine, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "VERIFICATION_USER", "COUNTER_USER"], module: "ANNAPRASADAM", page: "ANNA_VERIFY" },
  { to: "/anna/reports", label: "Annaprasadam Reports", icon: FileBarChart, roles: ALL, module: "ANNAPRASADAM", page: "ANNA_REPORTS" },
  { section: "Identity & Passes" },
  { to: "/identity/dashboard", label: "Identity Dashboard", icon: LayoutDashboard, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "SUPERVISOR"], module: "IDENTITY", page: "ID_DASHBOARD" },
  { to: "/identity/scan", label: "QR Scanner / Verify", icon: ScanLine, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "SUPERVISOR", "COUNTER_USER", "VERIFICATION_USER"], module: "IDENTITY", page: "ID_SCAN" },
  { to: "/identity/duty", label: "Duty Pass", icon: BadgeCheck, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "COUNTER_USER"], module: "IDENTITY", page: "ID_DUTY" },
  { to: "/identity/vehicle", label: "Vehicle Pass", icon: Car, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "COUNTER_USER"], module: "IDENTITY", page: "ID_VEHICLE" },
  { to: "/identity/children", label: "Children Tag", icon: Baby, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "COUNTER_USER", "VERIFICATION_USER"], module: "IDENTITY", page: "ID_CHILDREN" },
  { to: "/identity/pandits", label: "Pandits", icon: UserRound, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "COUNTER_USER"], module: "IDENTITY", page: "ID_PANDITS" },
  { to: "/identity/approvals", label: "Approvals", icon: ClipboardCheck, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN"], module: "IDENTITY", page: "ID_APPROVALS" },
  { to: "/identity/departments", label: "Departments", icon: Building2, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN"], module: "IDENTITY", page: "ID_MASTERS" },
  { to: "/identity/locations", label: "Locations / Ghats", icon: MapPin, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN"], module: "IDENTITY", page: "ID_MASTERS" },
  { to: "/identity/sacred-locations", label: "Sacred Locations", icon: Sparkles, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN"], module: "IDENTITY", page: "ID_MASTERS" },
  { to: "/identity/tag-ranges", label: "Tag Ranges", icon: QrCode, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN"], module: "IDENTITY", page: "ID_MASTERS" },
  { to: "/identity/reports", label: "Identity Reports", icon: FileBarChart, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "SUPERVISOR"], module: "IDENTITY", page: "ID_REPORTS" },
  { section: "Hundial" },
  { to: "/hundial/dashboard", label: "Hundial Dashboard", icon: LayoutDashboard, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "SUPERVISOR"], module: "HUNDIAL", page: "HUNDIAL_DASHBOARD" },
  { to: "/hundial/entry", label: "Hundial Opening", icon: FilePlus2, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN"], module: "HUNDIAL", page: "HUNDIAL_ENTRY" },
  { to: "/hundial/reports", label: "Hundial Reports", icon: Landmark, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN", "SUPERVISOR"], module: "HUNDIAL", page: "HUNDIAL_REPORTS" },
  { section: "Administration" },
  { to: "/masters", label: "Masters", icon: Database, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN"] },
  { to: "/login-logs", label: "Login Logs", icon: History, roles: ["SUPER_ADMIN", "TEMPLE_ADMIN"] },
  { to: "/data-manager", label: "Data Manager", icon: FileSearch, roles: ["SUPER_ADMIN"] },
];

const ROLE_LABEL = {
  SUPER_ADMIN: "Super Admin", TEMPLE_ADMIN: "Temple Admin", SUPERVISOR: "Supervisor",
  COUNTER_USER: "Counter User", VERIFICATION_USER: "Verification",
};

const ADMIN_ROLES = ["SUPER_ADMIN", "TEMPLE_ADMIN", "SUPERVISOR"];
const moduleOk = (user, mod) => !mod || ADMIN_ROLES.includes(user.role) || (user.modules || ["PROTOCOL", "SAREES"]).includes(mod);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isStaff = ["COUNTER_USER", "VERIFICATION_USER"].includes(user.role);
  const pages = userPages(user);
  const visible = NAV.filter((i) => {
    if (i.section) return true;
    if (!i.roles.includes(user.role) || !moduleOk(user, i.module)) return false;
    if (i.page && !pages.includes(i.page)) return false;
    if (i.anyPage && !i.anyPage.some((p) => pages.includes(p))) return false;
    if (isStaff) return !!i.module; // counter/godown staff: module pages only
    return true;
  });
  const items = visible.filter((v, idx) => !v.section || (visible[idx + 1] && !visible[idx + 1].section));
  // Sidebar: items grouped under their section headings.
  const groups = items.reduce((acc, it) => {
    if (it.section) acc.push({ section: it.section, items: [] });
    else if (acc.length) acc[acc.length - 1].items.push(it);
    else acc.push({ section: null, items: [it] });
    return acc;
  }, []);
  const [collapsed, setCollapsed] = useState(() => readCache("pd_sidebar_collapsed") === true);
  const [closedSections, setClosedSections] = useState(() => readCache("pd_sidebar_closed") || []);
  const toggleCollapsed = () => setCollapsed((c) => { writeCache("pd_sidebar_collapsed", !c); return !c; });
  const toggleSection = (name) => setClosedSections((cur) => {
    const next = cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name];
    writeCache("pd_sidebar_closed", next);
    return next;
  });
  const firstPage = items.find((i) => !i.section)?.to || "/";
  // Phone header on inner pages: back button + this page's name instead of the (cut-off) temple name.
  const pageItem = location.pathname === "/" ? null
    : items.filter((i) => !i.section && i.to !== "/" && location.pathname.startsWith(i.to)).sort((a, b) => b.to.length - a.to.length)[0]
      || (location.pathname === "/dashboard" ? { label: "Protocol Dashboard" } : { label: "Temple Management System" });

  // Phones have no sidebar: "/" shows a home screen of large buttons instead.
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const mobilePage = isMobile && pageItem;
  const templeKey = user.temple_id ? `pd_temple_${user.temple_id}` : null;
  const [branding, setBranding] = useState(() => readCache("pd_branding"));
  const [temple, setTemple] = useState(() => (templeKey ? readCache(templeKey) : null));
  const [brandReady, setBrandReady] = useState(() => !!readCache("pd_branding") && (!templeKey || !!readCache(templeKey)));
  useEffect(() => {
    const jobs = [api.get("/settings/branding").then((r) => { setBranding(r.data); writeCache("pd_branding", r.data); })];
    if (templeKey) {
      jobs.push(api.get("/temples").then((r) => {
        const t = r.data.find((x) => x.temple_id === user.temple_id);
        const keep = t ? { name: t.name, name_telugu: t.name_telugu, logo: t.logo, header_title: t.header_title, footer_text: t.footer_text } : null;
        setTemple(keep);
        writeCache(templeKey, keep);
      }));
    }
    Promise.allSettled(jobs).then(() => setBrandReady(true));
  }, []); // eslint-disable-line
  // Temple staff: wait for the temple's own logo rather than flashing the app logo first.
  const logo = temple?.logo || (templeKey && !temple && !brandReady ? null : branding?.logo);
  const headerTitle = temple?.header_title || branding?.header_title || "Temple Management System";
  const footerText = temple?.footer_text || branding?.footer_text || "Powered by Aatreya Infotech Systems · Temple ERP";

  useEffect(() => {
    // No Protocol Dashboard ticked (or counter staff): open the first page the user has instead of the home page.
    if ((isStaff || !pages.includes("PROTOCOL_DASHBOARD")) && !isMobile && location.pathname === "/" && firstPage !== "/")
      navigate(firstPage, { replace: true });
  }, [location.pathname, firstPage, isMobile]); // eslint-disable-line

  return (
    <div className="min-h-screen flex flex-col bg-cream" data-testid="app-layout">
      <header className="bg-gradient-to-r from-maroon-900 via-maroon-700 to-maroon-900 text-white sticky top-0 z-40 shadow-lg" data-testid="app-header">
        <div className="px-4 sm:px-6 py-2.5 flex items-center gap-3">
          {mobilePage ? (
            <>
              <button type="button" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))} aria-label="Back" data-testid="mobile-back-btn"
                className="w-10 h-10 -ml-1 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center active:scale-95 shrink-0">
                <ChevronLeft className="w-6 h-6 text-gold-300" />
              </button>
              <div className="min-w-0">
                <p className="font-semibold text-[15px] leading-tight truncate" data-testid="mobile-page-title">{mobilePage.label}</p>
                <p className="text-[11px] text-gold-300 leading-tight truncate">{user.temple_name || "All Temples"}</p>
              </div>
            </>
          ) : logo ? (
            <img src={logo} alt="logo" className="w-11 h-11 object-contain bg-white/90 rounded-md p-1" data-testid="header-brand-logo" />
          ) : !brandReady ? (
            <div className="w-11 h-11 rounded-md bg-white/10" />
          ) : (
            <>
              <div className="flex items-center gap-2 bg-white/10 rounded-md p-1.5">
                <Landmark className="w-6 h-6 text-gold-400" data-testid="gov-emblem" />
              </div>
              <div className="flex items-center gap-2 bg-gold-400/20 rounded-md p-1.5 border border-gold-400/40">
                <Flame className="w-6 h-6 text-gold-400" data-testid="temple-emblem" />
              </div>
            </>
          )}
          {!mobilePage && <div className="min-w-0">
            <h1 className="font-heading font-bold text-sm sm:text-lg leading-tight truncate" data-testid="header-temple-name">
              {user.temple_name || "All Temples"}
            </h1>
            <p className="text-[11px] text-gold-300 tracking-widest uppercase hidden sm:block" data-testid="header-brand-title">
              {headerTitle}
            </p>
          </div>}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="icon" className="text-gold-300 hover:text-white hover:bg-white/10" data-testid="nav-home-btn" onClick={() => navigate("/")}>
              <Home className="w-5 h-5" />
            </Button>
            <NotificationBell />
            <div className="hidden sm:flex items-center gap-2 bg-white/10 rounded-md px-3 py-1.5 border border-gold-400/30" data-testid="header-user-chip">
              <ShieldCheck className="w-4 h-4 text-gold-400" />
              <div className="text-xs leading-tight">
                <p className="font-semibold" data-testid="header-username">{user.full_name}</p>
                <p className="text-gold-300" data-testid="header-role">{ROLE_LABEL[user.role]}</p>
                {user.shift_name && <p className="text-gold-200 font-semibold" data-testid="header-shift">{user.shift_name}{user.shift_start ? ` · ${user.shift_start}–${user.shift_end}` : ""}</p>}
              </div>
            </div>
            <Button variant="ghost" size="icon" className="text-gold-300 hover:text-white hover:bg-white/10" data-testid="logout-btn"
              onClick={() => { logout(); navigate("/login"); }}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
        <div className="gold-divider" />
      </header>

      <div className="flex flex-1">
        <aside className={`${collapsed ? "w-[76px]" : "w-64"} shrink-0 hidden md:flex flex-col self-start sticky top-[66px] h-[calc(100vh-66px)] bg-gradient-to-b from-[#FFFDF8] to-[#FBF5EA] border-r border-[#E5D9C5] transition-[width] duration-200`} data-testid="app-sidebar">
          <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 space-y-1">
            {groups.map((g, gi) => {
              const open = collapsed || !g.section || !closedSections.includes(g.section);
              return (
                <div key={g.section || gi} className={g.section ? "pt-3" : ""}>
                  {g.section && (collapsed
                    ? <div className="mx-auto my-2 w-8 border-t border-gold-300" title={g.section} />
                    : <button type="button" onClick={() => toggleSection(g.section)} data-testid={`nav-section-${g.section.toLowerCase()}`}
                        className="w-full flex items-center gap-2 px-3 pb-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-gold-700 hover:text-maroon-800">
                        <span className="h-px w-3 bg-gold-400" />{g.section}
                        <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${open ? "" : "-rotate-90"}`} />
                      </button>)}
                  {open && g.items.map((item) => (
                    <NavLink key={item.to} to={item.to} end={item.end} title={collapsed ? item.label : undefined}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `group relative flex items-center gap-3 ${collapsed ? "justify-center px-0" : "px-2.5"} py-2 my-0.5 rounded-xl text-sm font-medium transition-all ${
                          isActive
                            ? "bg-gradient-to-r from-maroon-800 to-maroon-600 text-white shadow-md shadow-maroon-900/20"
                            : "text-slate-700 hover:bg-white hover:shadow-sm hover:text-maroon-800"
                        }`
                      }>
                      {({ isActive }) => (
                        <>
                          {isActive && <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-gold-400" />}
                          <span className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center transition-colors ${
                            isActive ? "bg-white/15 text-gold-300" : "bg-gold-50 text-maroon-700 group-hover:bg-maroon-50"}`}>
                            <item.icon className="w-[18px] h-[18px]" />
                          </span>
                          {!collapsed && <span className="truncate">{item.label}</span>}
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              );
            })}
          </nav>
          <div className="border-t border-[#E5D9C5] p-3 space-y-2">
            {!collapsed && (
              <div className="flex items-center gap-2.5 rounded-xl bg-white border border-[#EFE4CF] px-2.5 py-2 shadow-sm" data-testid="sidebar-user">
                <span className="w-9 h-9 shrink-0 rounded-full bg-maroon-700 text-gold-200 flex items-center justify-center text-sm font-bold">
                  {(user.full_name || user.username || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                </span>
                <div className="min-w-0 leading-tight">
                  <p className="text-sm font-semibold text-maroon-900 truncate">{user.full_name}</p>
                  <p className="text-[11px] text-gold-700 truncate">{ROLE_LABEL[user.role]}{user.temple_id ? ` · ${user.temple_id}` : ""}</p>
                  {user.shift_name && <p className="text-[11px] text-maroon-700 font-semibold truncate" data-testid="sidebar-shift">{user.shift_name}{user.shift_start ? ` (${user.shift_start}–${user.shift_end})` : ""}</p>}
                </div>
              </div>
            )}
            {!collapsed && <DownloadAppButton className="w-full py-2" />}
            <button type="button" onClick={toggleCollapsed} data-testid="sidebar-collapse"
              className="w-full flex items-center justify-center gap-2 rounded-lg py-1.5 text-xs text-slate-500 hover:text-maroon-800 hover:bg-white"
              title={collapsed ? "Expand menu" : "Collapse menu"}>
              {collapsed ? <ChevronsRight className="w-4 h-4" /> : <><ChevronsLeft className="w-4 h-4" /> Collapse menu</>}
            </button>
          </div>
        </aside>
        <main className="flex-1 p-4 sm:p-6 min-w-0">
          <OffShiftBanner user={user} />
          {isMobile && location.pathname === "/"
            ? <MobileHome items={items} user={user} logo={logo} templeName={temple?.name || (user.role === "SUPER_ADMIN" ? "All Temples" : "")} templeTelugu={temple?.name_telugu} />
            : <Outlet />}
        </main>
      </div>
      <footer className="text-center text-[11px] md:text-xs text-slate-400 md:text-slate-500 py-3 md:py-4 md:border-t md:border-[#E5D9C5] md:bg-white" data-testid="app-footer">
        {footerText}
      </footer>
    </div>
  );
}
